//
// Copyright (c) 2023 Emplifi, Inc.
//

import { first, Plugin } from "ckeditor5";
import AlignFigureCommand from "./alignFigureCommand";

interface AlignmentOption {
  name: string;
  style: Record<string, string>;
}

const alignmentOptions: Array<AlignmentOption> = [
  { name: "left", style: { "margin-left": "0", "margin-right": "auto" } },
  { name: "right", style: { "margin-left": "auto", "margin-right": "0" } },
  { name: "center", style: { "margin-left": "auto", "margin-right": "auto" } },
];

export default class AlignFigureEditing extends Plugin {
  // noinspection JSUnusedGlobalSymbols
  static get pluginName() {
    return "AlignFigureEditing";
  }

  init() {
    const editor = this.editor;
    this._setupConversion();
    editor.commands.add("alignFigure", new AlignFigureCommand(editor));
  }

  private _setupConversion() {
    const editor = this.editor;
    const schema = editor.model.schema;
    editor.conversion.for("downcast").add((dispatcher) =>
      dispatcher.on(
        `attribute:figureAlignment`,
        (evt, data, conversionApi) => {
          if (!conversionApi.consumable.consume(data.item, evt.name)) {
            return;
          }
          const viewWriter = conversionApi.writer;
          const figure = conversionApi.mapper.toViewElement(data.item);

          const alignment: AlignmentOption | undefined = this._getAlignmentDefinitionByName(
            data.attributeNewValue,
            alignmentOptions,
          );
          if (alignment) {
            for (const propertyName of Object.keys(alignment.style)) {
              viewWriter.setStyle(propertyName, alignment.style[propertyName], figure);
            }
            if (data.attributeOldValue) {
              viewWriter.removeClass("figure_" + data.attributeOldValue, figure);
            }
            viewWriter.addClass("figure_" + data.attributeNewValue, figure);
          }
        },
        { priority: "highest" },
      ),
    );

    schema.extend("media", { allowAttributes: "figureAlignment" });
    schema.extend("table", { allowAttributes: "figureAlignment" });
    schema.extend("imageBlock", { allowAttributes: "figureAlignment" });

    editor.conversion.for("upcast").add((dispatcher) =>
      dispatcher.on(`element:figure`, (evt, data, conversionApi) => {
        if (!data.modelRange) {
          return;
        }
        const viewElement = data.viewItem;
        const modelElement = first(data.modelRange.getItems());
        if (!modelElement) {
          return;
        }
        if (!conversionApi.schema.checkAttribute(modelElement, "figureAlignment")) {
          return;
        }
        for (const alignmentOption of alignmentOptions) {
          if (
            conversionApi.consumable.consume(viewElement, {
              classes: `figure_${alignmentOption["name"]}`,
            })
          ) {
            for (const style of Object.keys(alignmentOption["style"])) {
              conversionApi.consumable.consume(viewElement, {
                styles: style,
              });
            }
            conversionApi.writer.setAttribute(
              "figureAlignment",
              alignmentOption["name"],
              modelElement,
            );
            break;
          }
        }
      }),
    );
  }

  private _getAlignmentDefinitionByName(
    name: string,
    alignmentOptions: Array<AlignmentOption>,
  ): AlignmentOption | undefined {
    for (const option of alignmentOptions) {
      if (option.name === name) {
        return option;
      }
    }
  }
}
